import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchAllThankYouNotes,
  fetchThankYouNotes,
  toggleThankYouNoteFavorite,
} from "@/api/thankYouNotes";
import type { ThankYouNote, ThankYouNotesQuery } from "@/interfaces/thankYouNotes";

/**
 * Stable PREFIX of the query key, exported for the page's Retry button: TanStack matches keys by
 * prefix, so invalidating `["thankYouNotes"]` refetches whichever filter combination is on screen
 * (every page of it, for the pages already loaded).
 */
export const THANK_YOU_NOTES_QUERY_KEY = ["thankYouNotes"] as const;

/**
 * First page param. `cursor` is an OFFSET — how many rows the caller already holds — not a page
 * number, which is why the feed starts at 0 and not at 1.
 */
const FIRST_CURSOR = 0;

/**
 * Thank You Notes list (GFG-233/234) — the server-filtered, paged query plus the session's
 * favorites Set.
 *
 * PAGED, not one shot: the API caps a page at 100 rows (`MAX_PAGE_SIZE`) and answers with the full
 * filtered `total` and a `nextCursor`. `useInfiniteQuery` accumulates the pages the user asks for,
 * so the feed can grow past 100 while the counter still reports the real total. The accumulation
 * lives in the cache entry keyed by `query`, which is what makes a filter change RESET to page one
 * instead of appending rows from a different filter: a new filter is a new key, and a new key
 * starts at `initialPageParam`.
 *
 * `favoriteIds` starts empty and is seeded from each note's `favorite` field the first time the
 * query resolves (see the `useEffect` below): deriving it during render would re-seed on every
 * refetch and stomp any toggle the user already made in this session. `initialized` guards that
 * one-time seed without needing a ref, so a later refetch (e.g. `refetchOnMount`) never resets
 * favorites the user has already flipped locally.
 *
 * `enabled` (default true) holds the request back for a caller that has not yet earned it. Hooks
 * run top-to-bottom, before any `return` guard further down the component, so a page that redirects
 * an out-of-scope role would otherwise have already issued this authenticated, row-scoped GET by
 * the time it decides to bounce them. A disabled query never runs `queryFn`, keeps
 * `fetchStatus: "idle"` — hence `isLoading` false, not a spinner that never resolves — and leaves
 * `data` undefined, so `total` reads 0 and `unfilteredTotal` stays `undefined` rather than 0: the
 * caller can still tell "not asked yet" apart from "asked, and the answer was none".
 */
export function useThankYouNotes(
  query: ThankYouNotesQuery = {},
  { enabled = true }: { enabled?: boolean } = {},
) {
  const notesQuery = useInfiniteQuery({
    // The filters are part of the key, so changing search/campaign/dates starts a fresh page-one
    // query instead of re-filtering in memory. TanStack hashes the key structurally, so the fresh
    // object the page rebuilds each render is still the same cache entry.
    queryKey: [...THANK_YOU_NOTES_QUERY_KEY, query],
    queryFn: ({ pageParam }) =>
      fetchThankYouNotes({
        ...query,
        cursor: pageParam,
        // `campaignOptions` and `unfilteredTotal` are scope-wide and ignore the filters, so they
        // cannot change between pages of the same query: only page one pays for those two extra
        // reference queries.
        includeFilterOptions: query.includeFilterOptions && pageParam === FIRST_CURSOR,
      }),
    initialPageParam: FIRST_CURSOR,
    // null = exhausted, and TanStack reads that as "no next page". A cursor that does not move
    // forward is treated the same way (the guard `fetchAllThankYouNotes` uses for the export walk):
    // it would hand back rows already on screen, duplicating React keys instead of paging.
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.nextCursor === null || lastPage.nextCursor <= lastPageParam
        ? null
        : lastPage.nextCursor,
    // Hold the previously loaded pages on screen while the next filter's page one loads: without
    // it every keystroke drops the feed back to the loading skeleton and the grid flashes empty.
    // They are placeholder only — once the new page one resolves it REPLACES them, so nothing
    // accumulates across two different filters.
    placeholderData: keepPreviousData,
    enabled,
  });

  /**
   * Page one of the current query. The whole-result facts — the filtered `total`, and the
   * `includeFilterOptions` extras — are only asked for there, so this is where they are read from.
   */
  const firstPage = notesQuery.data?.pages[0];

  /**
   * Every page loaded so far, flattened and DE-DUPLICATED by `id` — exactly what the feed renders.
   *
   * Why the Map: `nextCursor` is an OFFSET, so page two is "rows 100..199 of the ordering as it
   * stands NOW". If that ordering shifted between the two fetches — a new note arrived, or the
   * Shopify order-creation cron bumped a legacy row's `updated_at`, promoting it under
   * `order by coalesce(thanks_at, updated_at) desc` — every row slides down one and the row that
   * was at index 99 comes back again at offset 100. `note.id` is the React key of both the card and
   * the list row, so a repeat is a key-collision warning plus a visibly duplicated note. First
   * occurrence wins, which keeps rows in the position the user already saw them in.
   *
   * The mirror case is deliberately NOT handled: a row demoted out of the top 100 by the same shift
   * is simply never returned. That gap is inherent to offset paging, and `nextCursor` staying an
   * offset rather than a keyset id is a settled decision — closing it means changing the API, not
   * this memo.
   */
  const notes = useMemo(() => {
    const byId = new Map<number, ThankYouNote>();
    for (const note of notesQuery.data?.pages.flatMap((page) => page.notes) ?? []) {
      if (!byId.has(note.id)) byId.set(note.id, note);
    }
    return [...byId.values()];
  }, [notesQuery.data]);

  const [favoriteIds, setFavoriteIds] = useState<ReadonlySet<number>>(new Set());
  const [initialized, setInitialized] = useState(false);

  /**
   * Per-note bookkeeping for the optimistic favorite toggle. `confirmed` is the last value the
   * server accepted (seeded from the response); `latestRequest` is the token of the newest PATCH
   * in flight for that note. Refs, not state: a revert must read what is true at the moment the
   * promise settles, and neither value should trigger a render on its own.
   */
  const confirmedFavorite = useRef(new Map<number, boolean>());
  const latestFavoriteRequest = useRef(new Map<number, number>());
  const favoriteRequestSeq = useRef(0);

  useEffect(() => {
    if (initialized || !notesQuery.data) return;
    // Every row of every GET answers `favorite: false` (nothing is persisted), so this seeds an
    // empty Set. It is kept because it is the seam persistence switches on at: the day the PATCH
    // stores anything, favorites hydrate from the response with no other change here.
    setFavoriteIds(new Set(notes.filter((n) => n.favorite).map((n) => n.id)));
    for (const note of notes) confirmedFavorite.current.set(note.id, note.favorite);
    setInitialized(true);
  }, [initialized, notesQuery.data, notes]);

  const toggleFavorite = useCallback(
    (id: number) => {
      // The next state is computed here, and the API call sits OUTSIDE the updater passed to
      // setFavoriteIds: React (StrictMode, in particular) can invoke a state updater more than
      // once per call, and a side effect inside it would fire the PATCH twice per click.
      const willFavorite = !favoriteIds.has(id);

      // Stamp this request so a settling promise can tell whether it is still the newest one for
      // this note. Comparing the Set's CURRENT value instead would be wrong twice over: a stale
      // rejection could undo a newer click the server accepted, and when two toggles of one note
      // both fail the landing state would depend on which rejected first.
      const token = ++favoriteRequestSeq.current;
      latestFavoriteRequest.current.set(id, token);

      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (willFavorite) next.add(id);
        else next.delete(id);
        return next;
      });

      toggleThankYouNoteFavorite(id, willFavorite)
        .then(() => {
          if (latestFavoriteRequest.current.get(id) !== token) return;
          latestFavoriteRequest.current.delete(id);
          confirmedFavorite.current.set(id, willFavorite);
        })
        .catch((error: unknown) => {
          // A real network call since GFG-234, so the optimistic star is rolled back when it
          // fails. The endpoint persists nothing, which means it only rejects on a genuine
          // network or auth failure — and the round trip is the only signal the user gets, so
          // leaving the star lit would claim a save that never happened, and the `initialized`
          // guard above means it would stay wrong until a full reload.
          console.error(`Failed to toggle favorite for thank-you note ${id}`, error);

          // Only the newest request for this note may revert; an older one settling late has
          // already been superseded by a click the user made afterwards.
          if (latestFavoriteRequest.current.get(id) !== token) return;
          latestFavoriteRequest.current.delete(id);

          // Restore the last value the server ACCEPTED, not the value this click replaced.
          // Undoing one step would land on the previous optimistic state, which may itself have
          // failed — two failed toggles in a row would then leave the star showing a save that
          // never happened either.
          const confirmed = confirmedFavorite.current.get(id) ?? false;
          setFavoriteIds((prev) => {
            if (prev.has(id) === confirmed) return prev; // React bail-out: no re-render.
            const next = new Set(prev);
            if (confirmed) next.add(id);
            else next.delete(id);
            return next;
          });
        });
    },
    [favoriteIds],
  );

  const { fetchNextPage, hasNextPage, isFetching } = notesQuery;

  /**
   * Append the next page to the ones already on screen. Guarded rather than trusting the button's
   * `disabled`: a click landing while a filter change is still in flight would page the OLD
   * placeholder result, appending rows that do not match the filter the user just applied.
   */
  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetching) return;
    void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetching]);

  /**
   * Every note matching the CURRENT filters, for the CSV export — the loaded pages are only what
   * the user has asked to see. Deliberately not memoised: `query` is a new object on every render,
   * so a `useCallback` around it would churn its identity anyway.
   */
  async function fetchAllNotes(): Promise<ThankYouNote[]> {
    return fetchAllThankYouNotes(query);
  }

  return {
    notes,
    /**
     * The server's count of everything matching the current filters — NOT `notes.length`, which is
     * only the pages fetched so far. This is the number the counter and the CSV export agree on.
     */
    total: firstPage?.total ?? 0,
    campaignOptions: firstPage?.campaignOptions ?? [],
    /** `undefined` until page one has answered, so the page can tell it apart from a real 0. */
    unfilteredTotal: firstPage?.unfilteredTotal,
    isLoading: notesQuery.isLoading,
    // True while a filter change is in flight and `placeholderData` is still showing the old pages,
    // so the page can mark the feed as stale instead of pretending it is current. Also true during
    // a "load more", which the page separates out via `isFetchingNextPage`.
    isFetching: notesQuery.isFetching,
    isError: notesQuery.isError,
    hasNextPage: notesQuery.hasNextPage,
    isFetchingNextPage: notesQuery.isFetchingNextPage,
    loadMore,
    favoriteIds,
    toggleFavorite,
    fetchAllNotes,
  };
}
